import { useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { client } from 'modules/Apollo/client';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';
import {
  selectedHangingProtocolPayloadSelector,
  selectedHangingProtocolSmidSelector,
} from './state';
import type { HangingProtocol } from './useHangingProtocol';

// The study groups a payload is resolved against. Taken from the caller rather than derived here:
// GET_HANGING_PROTOCOL resolves viewport study/series/stack references against these, so they have
// to be the same groups the caller matched the HP against.
export type SelectedHangingProtocolQueryTarget = {
  cases: Array<Array<string>>;
} & WithApolloContext;

export type UseSelectedHangingProtocolWriters = {
  selectHangingProtocol: (smid: string) => Promise<void>;
  refreshSelectedHangingProtocol: () => Promise<void>;
  clearSelectedHangingProtocol: () => void;
};

// One in-flight resolve per case+smid. useHangingProtocol mounts in several components at once
// (widget, layouts, viewport classifications), so without this every one of them would issue the
// same GET_HANGING_PROTOCOL on a reload.
const resolvesInFlight = new Set<string>();

/**
 * Resolves a case's HP selection to a payload, given the server-matched HP for that case.
 *
 * The selection is an smid; this decides where the matching payload comes from:
 *
 * - the server match itself, when it *is* the selected HP. This is the common case and needs no
 *   extra fetch, and it is what keeps an applied HP fresh through a refetch for free.
 * - the per-case payload cache otherwise. The two diverge when the server match sits on a different
 *   protocol than the user's choice — a criteria tie, where two HPs share study/classification
 *   criteria and the match keeps returning the older one (VX-4713).
 *
 * A cached payload for some *other* smid is ignored rather than returned: it belongs to a selection
 * that has since changed. Returning null here is what asks `useSelectedHangingProtocol` to fetch.
 */
export const resolveSelectedHangingProtocol = ({
  selectedSmid,
  cachedPayload,
  matchedHangingProtocol,
}: {
  selectedSmid: Nullable<string>;
  cachedPayload: Nullable<HangingProtocol>;
  matchedHangingProtocol: Nullable<HangingProtocol>;
}): Nullable<HangingProtocol> => {
  if (selectedSmid == null) return null;
  if (matchedHangingProtocol?.smid === selectedSmid) return matchedHangingProtocol;
  return cachedPayload?.smid === selectedSmid ? cachedPayload : null;
};

export const useSelectedHangingProtocolSmid = (): Nullable<string> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
};

/**
 * Read-only resolution of the selection, with no fetching. Use this where the payload is only being
 * observed; `useSelectedHangingProtocol` is the variant that also fills the cache.
 */
export const useResolvedSelectedHangingProtocol = (
  matchedHangingProtocol: Nullable<HangingProtocol>
): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  const selectedSmid = useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
  const cachedPayload = useRecoilValue(selectedHangingProtocolPayloadSelector(currentCaseSmid));
  return resolveSelectedHangingProtocol({ selectedSmid, cachedPayload, matchedHangingProtocol });
};

export const useSelectedHangingProtocolWriters = ({
  cases,
  context,
}: SelectedHangingProtocolQueryTarget): UseSelectedHangingProtocolWriters => {
  const currentCaseSmid = useCurrentCaseId();

  // Resolves one HP smid to a payload for the current case.
  //
  // Fetches with `cases` so viewport study/series/stack references resolve against the current
  // case. A fetch WITHOUT cases returns under-resolved viewports and paints "series incompatible"
  // errors in the grid. no-cache for the reason previewing uses it: GET_HANGING_PROTOCOL resolves
  // against the current case, and writing that into the shared normalized cache would corrupt the
  // canonical (non-case-resolved) entity the HP list table reads.
  const fetchHangingProtocolForCase = useCallback(
    async (smid: string): Promise<Nullable<HangingProtocol>> => {
      if (currentCaseSmid == null || cases.length === 0) return null;
      const inFlightKey = `${currentCaseSmid}:${smid}`;
      if (resolvesInFlight.has(inFlightKey)) return null;
      resolvesInFlight.add(inFlightKey);
      try {
        const { data } = await client.query({
          query: GET_HANGING_PROTOCOL,
          variables: { id: smid, cases },
          context: {
            ...context,
            telemetry: {
              ...(context.telemetry ?? {}),
              fetchPolicy: 'no-cache',
            },
          },
          fetchPolicy: 'no-cache',
        });
        return data?.hangingProtocol ?? null;
      } catch (error) {
        logger.error('Failed to resolve the selected hanging protocol', error);
        return null;
      } finally {
        resolvesInFlight.delete(inFlightKey);
      }
    },
    [cases, context, currentCaseSmid]
  );

  // Records the user's choice and primes its payload. Used when the HP the viewer should show is
  // decided locally rather than by the server match — confirming a preview, or creating an HP the
  // server's post-create match ties with an existing same-criteria one and keeps auto-matching the
  // older of (VX-4713).
  //
  // The smid is written only once the payload is in hand, and both in the same commit. Writing the
  // smid first opens a window where the case has a selection with nothing to show for it, and the
  // resolver falls back to the match for the duration -- which on a criteria tie is a *different*
  // protocol, so the effective HP smid changes and useHangingProtocol re-initializes the step bar
  // from the wrong protocol (the VX-4770 failure mode, reached transiently instead of permanently).
  const selectHangingProtocol = useRecoilCallback(
    ({ set }) =>
      async (smid: string) => {
        if (currentCaseSmid == null) return;
        const hangingProtocol = await fetchHangingProtocolForCase(smid);
        // Leave the selection alone on a failed fetch rather than pointing it at nothing.
        if (hangingProtocol == null) return;
        set(selectedHangingProtocolSmidSelector(currentCaseSmid), smid);
        set(selectedHangingProtocolPayloadSelector(currentCaseSmid), hangingProtocol);
      },
    [currentCaseSmid, fetchHangingProtocolForCase]
  );

  // "This copy is stale" — re-resolves the selection's payload from the server. This is what a
  // caller wants after anything that changes the HP record (an edit-form Update) or reloads the
  // case's data (Reset All).
  //
  // Reads the selected smid itself, so no caller has to capture and re-pin one; and it cannot lose
  // the user's choice, which is the whole point of the split. Overwrites rather than clearing first,
  // for the same no-gap reason as selectHangingProtocol above.
  const refreshSelectedHangingProtocol = useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        if (currentCaseSmid == null) return;
        const selectedSmid = snapshot
          .getLoadable(selectedHangingProtocolSmidSelector(currentCaseSmid))
          .getValue();
        if (selectedSmid == null) return;
        const hangingProtocol = await fetchHangingProtocolForCase(selectedSmid);
        if (hangingProtocol == null) return;
        set(selectedHangingProtocolPayloadSelector(currentCaseSmid), hangingProtocol);
      },
    [currentCaseSmid, fetchHangingProtocolForCase]
  );

  // "Forget the choice" — used when the selection itself is no longer valid, i.e. the selected HP
  // has been deleted. originalHangingProtocol then falls back to the server-matched HP.
  const clearSelectedHangingProtocol = useRecoilCallback(
    ({ reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        reset(selectedHangingProtocolSmidSelector(currentCaseSmid));
        reset(selectedHangingProtocolPayloadSelector(currentCaseSmid));
      },
    [currentCaseSmid]
  );

  return {
    selectHangingProtocol,
    refreshSelectedHangingProtocol,
    clearSelectedHangingProtocol,
  };
};

/**
 * The selected HP's payload, fetching it when the cache cannot supply one.
 *
 * This is what makes the selection self-sufficient: a refresh restores only the smid, and a stale
 * payload can be dropped by whoever notices, because resolution happens here rather than at the
 * call site. No caller has to re-pin an smid to keep a selection alive through a refetch.
 */
export const useSelectedHangingProtocol = ({
  matchedHangingProtocol,
  cases,
  context,
}: {
  matchedHangingProtocol: Nullable<HangingProtocol>;
} & SelectedHangingProtocolQueryTarget): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  const selectedSmid = useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
  const cachedPayload = useRecoilValue(selectedHangingProtocolPayloadSelector(currentCaseSmid));
  const { refreshSelectedHangingProtocol } = useSelectedHangingProtocolWriters({ cases, context });

  const selectedHangingProtocol = resolveSelectedHangingProtocol({
    selectedSmid,
    cachedPayload,
    matchedHangingProtocol,
  });

  // There is a selection but nothing to show for it: the case was reloaded (only the smid persists)
  // or the payload was refreshed away. Resolve it.
  const needsPayload = selectedSmid != null && selectedHangingProtocol == null;

  // Held in a ref so the effect fires on the *need* appearing rather than on the writer's identity.
  // The writer closes over `cases`, which changes as studies load, and a fetch that came back empty
  // leaves `needsPayload` true — depending on it directly would retry on every one of those renders
  // instead of once.
  const refreshRef = useRef(refreshSelectedHangingProtocol);
  refreshRef.current = refreshSelectedHangingProtocol;

  useEffect(() => {
    if (!needsPayload) return;
    void refreshRef.current();
  }, [needsPayload]);

  return selectedHangingProtocol;
};
